import { AsyncButton, Button } from '@components/button';
import { Form, FormFileUpload } from '@components/form';
import { useFileUpload } from '@fileUpload/FileUpload';
import { useUploadVolumeDataThumbnailMutation } from '@generated/graphql';
import { Stack } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import { useState } from 'react';

interface ThumbnailUploadModalProps {
  refetchVolumeData: () => Promise<unknown>;
  directory: string[];
  onClose: () => void;
  name: string;
  volumeId: string;
}

export const ThumbnailUploadModal = ({
  onClose,
  volumeId,
  refetchVolumeData,
  name,
  directory,
}: ThumbnailUploadModalProps) => {
  const [isUploadStarted, setIsUploadStarted] = useState<boolean>(false);
  const { useAsyncNotification, addNotification } = useNotifications();
  const [uploadVolumeDataThumbnail] = useUploadVolumeDataThumbnailMutation();
  const uploadFile = useFileUpload();

  const uploadVolumeThumbnailAsyncFunction = useAsyncNotification(
    'Volume thumbnail uploaded successfully.',
    async ({ file }: { file: File }) => {
      if (!file) {
        return;
      }

      const { data } = await uploadVolumeDataThumbnail({
        variables: {
          volumeId,
          key: `${directory.join('')}${name}`,
        },
      });

      if (!data?.uploadVolumeDataThumbnail?.url) {
        return;
      }
      const convertedFile = await convertImageToPng(file);
      await uploadFile({
        file: convertedFile,
        urls: [data?.uploadVolumeDataThumbnail?.url],
        partSize: 0,
      });

      addNotification({
        text: 'The thumbnail will appear soon.',
        type: 'info',
        duration: 10000,
      });
      onClose();
    },
    { loadingMessage: 'Uploading the Volume thumbnail...' },
  );

  const submitVolumeThumbnail = async (formData: { file: File }) => {
    setIsUploadStarted(true);
    await uploadVolumeThumbnailAsyncFunction(formData);
    void refetchVolumeData();
    setIsUploadStarted(false);
  };

  return (
    <Form initialValues={{ file: null }} onSubmit={submitVolumeThumbnail}>
      {({ handleSubmit, values }) => (
        <Stack>
          <FormFileUpload
            name="file"
            label="Select file"
            accept="image/png, image/gif, image/jpeg"
          />
          <Stack gap={4}>
            <AsyncButton
              disabled={!values?.file || isUploadStarted}
              fullWidth
              type="submit"
              data-cy="VolumeThumbnails-Upload-Modal-Upload-Button"
              onClick={handleSubmit}
            >
              Upload
            </AsyncButton>
            <Button
              fullWidth
              variant="secondary"
              onClick={onClose}
              data-cy="VolumeThumbnails-Upload-Modal-Cancel-Button"
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      )}
    </Form>
  );
};

// Convert image to PNG and scale it to 512x512
const convertImageToPng = async (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const image = new Image();
      image.src = event.target.result as string;
      image.onload = () => {
        let targetWidth = image.width;
        let targetHeight = image.height;

        const maxDimension = 512;
        if (image.width > maxDimension || image.height > maxDimension) {
          if (image.width > image.height) {
            targetWidth = maxDimension;
            targetHeight = (image.height / image.width) * maxDimension;
          } else {
            targetHeight = maxDimension;
            targetWidth = (image.width / image.height) * maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(image, 0, 0, targetWidth, targetHeight);
          canvas.toBlob((blob) => {
            if (blob) {
              const pngFile = new File([blob], file.name.replace(/\.[^.]+$/, '.png'), {
                type: 'image/png',
              });
              resolve(pngFile);
            }
          }, 'image/png');
        } else {
          reject(new Error('Could not get canvas context.'));
        }
      };
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};
